import { useEffect, useMemo, useState } from 'react';
import { dataTemplate, type PerfScript } from '@1qa/shared';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Copy, Gauge, KeyRound, RotateCcw, Save, Trash2 } from '@/components/icons';
import { ScriptEditor, type NewTestData } from '@/components/perf/ScriptEditor';
import { ScriptChecks } from '@/components/perf/ScriptChecks';
import { TemplateButton } from '@/components/perf/DataFiles';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { timeAgo } from '@/lib/format';
import { SCRIPT_SOURCE } from '@/lib/perf';
import { Badge, Button, Card, CardBody, CardHeader, ErrorNotice, Field, Input, LinkButton, PageHeader, PageLoader, Textarea, useConfirm, useToast } from '@/components/ui';

/**
 * A script of the project's library: its requests step by step, editable by its creator or a Project Admin
 * (parameterization, correlation, checks; components/perf/ScriptEditor.tsx), the test data it uses and the tests using it.
 */
export function PerfScriptPage() {
  const { projectId = '', scriptId = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const { can, canModify } = useAuth();
  const project = useQuery({ queryKey: ['project', projectId], queryFn: () => api.project(projectId) });
  const script = useQuery({ queryKey: ['perf-script', scriptId], queryFn: () => api.perfScript(scriptId) });
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  // Unsaved edits of the requests; null: none.
  const [draft, setDraft] = useState<PerfScript | null>(null);
  const variables = useQuery({ queryKey: ['project-variables', projectId], queryFn: () => api.projectVariables(projectId) });
  const testData = useMemo(() => new Map((variables.data ?? []).map((v) => [v.key, v.isSecret])), [variables.data]);
  useEffect(() => {
    if (!draft) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [draft]);
  useEffect(() => {
    if (!script.data) return;
    setName(script.data.name);
    setDescription(script.data.description ?? '');
  }, [script.data]);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['perf-script', scriptId] });
    qc.invalidateQueries({ queryKey: ['perf-scripts', projectId] });
  };
  const save = useMutation({ mutationFn: () => api.updatePerfScript(scriptId, { name: name.trim(), description: description.trim() || null }), onSuccess: () => (toast.success('Saved'), refresh()), onError: (e) => toast.error(errorMessage(e)) });
  const saveScript = useMutation({
    mutationFn: (next: PerfScript) => api.updatePerfScript(scriptId, { script: next }),
    onSuccess: () => {
      toast.success('Script saved');
      setDraft(null);
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const addTestData = useMutation({
    mutationFn: (v: NewTestData) => api.createProjectVariable(projectId, { key: v.key, value: v.value, isSecret: v.isSecret }),
    onSuccess: (v) => {
      toast.success(`Saved ${v.key} as test data of the project`);
      qc.invalidateQueries({ queryKey: ['project-variables', projectId] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const copy = useMutation({ mutationFn: () => api.copyPerfScript(scriptId), onSuccess: (s) => (refresh(), navigate(`/projects/${projectId}/perf/scripts/${s.id}`)), onError: (e) => toast.error(errorMessage(e)) });
  const remove = useMutation({
    mutationFn: () => api.deletePerfScript(scriptId),
    onSuccess: () => {
      refresh();
      toast.success('Script deleted');
      navigate(`/projects/${projectId}?tab=scripts`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (project.isPending || script.isPending) return <PageLoader />;
  if (project.isError) return <ErrorNotice error={project.error} onRetry={() => project.refetch()} />;
  if (script.isError) return <ErrorNotice error={script.error} onRetry={() => script.refetch()} />;
  const p = project.data;
  const s = script.data;
  const ir = s.script;
  const mine = canModify('test.write', s.createdById, p.orgId);
  const data = ir.dataKeys.filter((k) => k !== 'APP_URL');
  const changed = name.trim() !== s.name || description.trim() !== (s.description ?? '');
  const template = dataTemplate(ir);

  return (
    <div className="space-y-6">
      <Link to={`/projects/${projectId}?tab=scripts`} className="inline-flex items-center gap-1.5 text-sm text-fg-secondary hover:text-fg">
        <ArrowLeft className="size-4" /> {p.name} · Scripts
      </Link>
      <PageHeader
        eyebrow="Performance script"
        title={s.name}
        description={`${SCRIPT_SOURCE[s.source]} · ${s.transactions} transactions, ${s.requests} requests · updated ${timeAgo(s.updatedAt)}${s.createdBy ? ` by ${s.createdBy.name}` : ''}`}
        actions={
          <>
            {can('test.write', p.orgId) && (
              <Button variant="outline" onClick={() => copy.mutate()} loading={copy.isPending} icon={<Copy className="size-4" />}>
                Make a copy
              </Button>
            )}
            {mine && (
              <Button
                variant="ghost"
                aria-label="Delete this script"
                disabled={s.usedBy.length > 0}
                title={s.usedBy.length ? 'Used by a performance test' : undefined}
                onClick={async () => {
                  if (await confirm({ title: 'Delete this script?', message: `“${s.name}” will be deleted.`, confirmLabel: 'Delete' })) remove.mutate();
                }}
                icon={<Trash2 className="size-4" />}
              />
            )}
            {can('test.write', p.orgId) && (
              <LinkButton to={`/projects/${projectId}/perf/new?script=${encodeURIComponent(s.id)}`} icon={<Gauge className="size-4" />}>
                Use in a new performance test
              </LinkButton>
            )}
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
        <Card>
          <CardHeader title="About" />
          <CardBody className="space-y-4">
            <Field label="Name">{(id) => <Input id={id} value={name} maxLength={200} disabled={!mine} onChange={(e) => setName(e.target.value)} />}</Field>
            <Field label="Description">{(id) => <Textarea id={id} rows={2} value={description} maxLength={4000} disabled={!mine} onChange={(e) => setDescription(e.target.value)} />}</Field>
            {mine && (
              <div className="flex justify-end">
                <Button onClick={() => save.mutate()} disabled={!changed || !name.trim()} loading={save.isPending} icon={<Save className="size-4" />}>
                  Save
                </Button>
              </div>
            )}
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="Used by" description="Performance tests whose scenarios run this script." />
          <CardBody className="space-y-3 text-sm">
            {s.usedBy.length === 0 ? (
              <p className="text-fg-secondary">No performance test uses it yet.</p>
            ) : (
              <ul className="space-y-1">
                {s.usedBy.map((t) => (
                  <li key={t.id}>
                    <Link to={`/projects/${projectId}/perf/${t.id}`} className="text-link hover:underline">
                      {t.name}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {(data.length > 0 || ir.secretKeys.length > 0) && (
              <>
              <p className="flex flex-wrap items-center gap-2 text-fg-secondary">
                <KeyRound className="size-4 text-primary" aria-hidden /> Test data:
                {data.map((k) => (
                  <Badge key={k}>{k}</Badge>
                ))}
                {ir.secretKeys.map((k) => (
                  <Badge key={k} tone="warning">
                    {k} (secret)
                  </Badge>
                ))}
              </p>
              <div className="space-y-1">
                <TemplateButton name={s.name} {...template} />
                <p className="text-xs text-fg-muted">Fill in one row per journey (for example one user’s sign-in per row) and upload it under Scripts › Test data files.</p>
              </div>
              </>
            )}
          </CardBody>
        </Card>
      </div>

      <ScriptChecks projectId={projectId} scriptId={scriptId} canRun={can('perf.run', p.orgId)} unsaved={draft !== null} />

      <Card>
        <CardHeader
          title="Requests, step by step"
          description={
            mine
              ? 'One user’s journey; every user of a scenario repeats it. Open a request to edit it, make values test data, or take values from its response for later requests.'
              : 'One user’s journey; every user of a scenario repeats it. Only the person who made the script or a Project Admin can change it: make a copy to edit your own.'
          }
          actions={
            draft && (
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setDraft(null)} icon={<RotateCcw className="size-4" />}>
                  Discard changes
                </Button>
                <Button onClick={() => saveScript.mutate(draft)} loading={saveScript.isPending} icon={<Save className="size-4" />}>
                  Save changes
                </Button>
              </div>
            )
          }
        />
        <CardBody className="space-y-4">
          {draft && s.usedBy.length > 0 && (
            <p className="text-sm text-fg-secondary">
              Saved changes apply to the next runs of {s.usedBy.length === 1 ? `“${s.usedBy[0]!.name}”` : `${s.usedBy.length} performance tests`}.
            </p>
          )}
          <ScriptEditor
            projectId={projectId}
            script={draft ?? ir}
            onChange={setDraft}
            readOnly={!mine}
            testData={testData}
            onNewTestData={(v) => addTestData.mutate(v)}
            notify={(m) => toast.info(m)}
          />
        </CardBody>
      </Card>
    </div>
  );
}
